import { useEffect, useRef, useState } from "react";

// Best-effort attribution based on where the URL came from.
function attributionFor(url: string): string {
  if (url.includes("upload.wikimedia.org") || url.includes("wikimedia.org")) {
    return "Photo: Wikimedia Commons (CC)";
  }
  if (url.includes("/places/streetview") || url.includes("/places/photo")) {
    return "Photo: Google";
  }
  return "Photo";
}

interface PhotoLightboxProps {
  photos: string[]; // resolved URLs
  startIndex: number;
  caption?: string;
  onClose: () => void;
}

// Full-viewport photo viewer. Mobile: swipe left/right to navigate,
// swipe down to dismiss, tap to toggle chrome. Desktop: arrow keys,
// Esc, visible prev/next/close buttons, clickable thumbnail strip.
export function PhotoLightbox({
  photos,
  startIndex,
  caption,
  onClose,
}: PhotoLightboxProps) {
  const [idx, setIdx] = useState(startIndex);
  const [chromeVisible, setChromeVisible] = useState(true);
  const [dragY, setDragY] = useState(0);
  const touchStartRef = useRef<{ x: number; y: number; t: number } | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  const prev = () => setIdx((i) => (i - 1 + photos.length) % photos.length);
  const next = () => setIdx((i) => (i + 1) % photos.length);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    // Prevent body scroll while open.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Preload neighbors for instant nav feel.
  useEffect(() => {
    if (photos.length <= 1) return;
    [-1, 1].forEach((d) => {
      const i = (idx + d + photos.length) % photos.length;
      const img = new Image();
      img.src = photos[i];
    });
  }, [idx, photos]);

  // Touch handling for swipe nav + swipe-down dismiss.
  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    touchStartRef.current = { x: t.clientX, y: t.clientY, t: Date.now() };
    setDragY(0);
  };
  const onTouchMove = (e: React.TouchEvent) => {
    const start = touchStartRef.current;
    if (!start) return;
    const t = e.touches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    // Track vertical drag for dismiss preview if the motion is
    // dominantly vertical and downward.
    if (Math.abs(dy) > Math.abs(dx) && dy > 0) {
      setDragY(dy);
    }
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    const start = touchStartRef.current;
    touchStartRef.current = null;
    if (!start) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    const elapsed = Date.now() - start.t;
    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);

    // Swipe-down dismiss: > 120px or momentum > 0.5px/ms downward.
    if (dy > 120 || (dy > 60 && dy / elapsed > 0.5)) {
      onClose();
      return;
    }
    setDragY(0);

    // Horizontal swipe nav: > 50px and dominantly horizontal.
    if (absDx > 50 && absDx > absDy && photos.length > 1) {
      if (dx > 0) prev();
      else next();
    }
  };

  const safeIdx = Math.max(0, Math.min(idx, photos.length - 1));
  const current = photos[safeIdx];

  // Dismiss-drag visual: slide image down, fade backdrop.
  const backdropOpacity = Math.max(0.3, 1 - dragY / 400);
  const imgTranslate = dragY;

  return (
    <div
      className="fixed inset-0 z-50 select-none"
      role="dialog"
      aria-modal="true"
      aria-label="Photo viewer"
    >
      <div
        className="absolute inset-0 bg-black transition-opacity"
        style={{ opacity: backdropOpacity }}
        onClick={onClose}
      />

      {/* Image stage */}
      <div
        className="relative h-full w-full flex items-center justify-center"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onClick={(e) => {
          // Only toggle chrome when tapping the background/image,
          // not the controls themselves.
          if (e.target === e.currentTarget || e.target === imgRef.current) {
            setChromeVisible((v) => !v);
          }
        }}
      >
        <img
          ref={imgRef}
          src={current}
          alt=""
          className="max-h-[90vh] max-w-[95vw] object-contain transition-transform"
          style={{
            transform: `translateY(${imgTranslate}px)`,
            transitionDuration: dragY === 0 ? "200ms" : "0ms",
          }}
        />

        {/* Chrome: close, counter, attribution, nav arrows, dots/strip */}
        {chromeVisible && (
          <>
            <button
              onClick={onClose}
              aria-label="Close"
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/50 border border-white/25 text-white flex items-center justify-center hover:bg-black/75"
            >
              ✕
            </button>

            {photos.length > 1 && (
              <>
                <button
                  onClick={prev}
                  aria-label="Previous photo"
                  className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 border border-white/25 text-white items-center justify-center hover:bg-black/75 text-xl"
                >
                  ‹
                </button>
                <button
                  onClick={next}
                  aria-label="Next photo"
                  className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 border border-white/25 text-white items-center justify-center hover:bg-black/75 text-xl"
                >
                  ›
                </button>
              </>
            )}

            <div className="absolute bottom-4 left-0 right-0 flex flex-col items-center gap-3 text-white">
              {/* Counter + caption */}
              <div className="flex items-center gap-3 px-3 py-1 rounded-full bg-black/60 border border-white/20 text-xs backdrop-blur-sm">
                <span>
                  {safeIdx + 1} / {photos.length}
                </span>
                {caption && (
                  <>
                    <span className="text-white/30">·</span>
                    <span className="max-w-[60vw] truncate">{caption}</span>
                  </>
                )}
                <span className="text-white/30">·</span>
                <span className="text-white/75">{attributionFor(current)}</span>
              </div>

              {/* Thumbnail strip (desktop only; mobile relies on swipe) */}
              {photos.length > 1 && (
                <div className="hidden md:flex gap-1.5 max-w-[80vw] overflow-x-auto px-2 py-1 rounded-lg bg-black/40 backdrop-blur-sm">
                  {photos.map((p, i) => (
                    <button
                      key={p + i}
                      onClick={() => setIdx(i)}
                      aria-label={`Photo ${i + 1}`}
                      className={`shrink-0 w-14 h-14 rounded-md overflow-hidden border-2 transition-colors ${
                        i === safeIdx
                          ? "border-accent"
                          : "border-transparent hover:border-white/50"
                      }`}
                    >
                      <img
                        src={p}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
